import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowRight, ArrowLeft } from "lucide-react";
import useSEO from "@/hooks/useSEO";
import OntologyGraph from "@/components/OntologyGraph";

/* Home v3 — clone structurel de palantir.com :
   carrousel Newsroom → phrase de mission → Nos logiciels → graphe signature
   → grille bas de page → double CTA géant. */
const FR = {
  seo_t: "Noam · L'infrastructure de décision. Opéré depuis Genève.",
  seo_d: "Nos logiciels alimentent des décisions critiques, en temps réel, pilotées par l'IA, de l'origination d'un deal à la salle du conseil. Opéré depuis Genève.",
  news: [
    { tick: "Noam Deal", label: "Noam Deal", h: "La note de réalisabilité : chaque deal noté, sur pièces.", img: "/home/news_deal.jpg", to: "/platform" },
    { tick: "Relations 360", label: "Relations Stratégiques 360", h: "Un groupe entier, ses hommes, ses actifs, ses inconnues, sur un seul écran.", img: "/home/news_360.jpg", to: "/platform" },
    { tick: "Preuves jointes", label: "Preuves jointes", h: "La note monte sur pièces, pas sur promesses.", img: "/home/news_evidence.jpg", to: "/pourquoi" },
    { tick: "Noam Camp", label: "Noam Camp", h: "Deux heures, vos documents, un monde relié. Repartez avec la preuve.", img: "/home/news_camp.jpg", to: "/camp" },
    { tick: "Miroir du matin", label: "Miroir du matin", h: "Le brief quotidien qui connaît vos dossiers.", img: "/home/news_mirror.jpg", to: "/platform" },
  ],
  mission: "Nos logiciels alimentent des décisions critiques, en temps réel, pilotées par l'IA, dans les banques, les family offices et les directions générales, de l'origination d'un deal à la salle du conseil.",
  sw_ov: "Nos logiciels",
  sw_more: "En savoir plus",
  systems: [
    ["Transactions privées de bout en bout, de l'origination au closing.", "Courtiers, intermédiaires, family offices, banques"],
    ["Comprendre les marchés et piloter ses investissements.", "Gérants, desks, investisseurs, trésoreries"],
    ["Piloter l'entreprise, maîtriser ses opérations, gouverner ses décisions.", "Dirigeants, CFO, risk & compliance officers"],
  ],
  plat_name: "Noam Platform",
  plat_sub: "Le socle commun : ontologie, intelligence, IA, confiance, réseau, données.",
  plat_link: "Découvrir la plateforme",
  onto_ov: "L'ontologie",
  onto_t: "Un monde relié.",
  onto_lead: "Chaque objet existe une seule fois. Cliquez : sociétés, deals, événements et risques s'éclairent ensemble.",
  grid: [
    ["Offres", "Plateforme, transaction, infrastructure : vos coûts suivent vos deals.", "/request-access"],
    ["Études d'impact", "Les trois problèmes que Noam résout, et ce qu'ils coûtent.", "/pourquoi"],
    ["Capacités", "Ontologie, intelligence, IA, confiance, réseau, données.", "/platform"],
    ["Documents", "Le document technique de l'infrastructure Noam.", "/docs/noam-document-technique.html"],
  ],
  cta_l_t: "Demander une démo",
  cta_l_s: "Voir Noam sur vos propres dossiers, pas sur une démo générique.",
  cta_r_t: "Venir au Noam Camp",
  cta_r_s: "Deux heures, vos documents, un monde relié. Repartez avec la preuve.",
};

const EN = {
  seo_t: "Noam · The decision infrastructure. Operated from Geneva.",
  seo_d: "Our software powers real-time, AI-driven critical decisions, from deal origination to the boardroom. Operated from Geneva.",
  news: [
    { tick: "Noam Deal", label: "Noam Deal", h: "The feasibility rating: every deal graded, on evidence.", img: "/home/news_deal.jpg", to: "/platform" },
    { tick: "Relations 360", label: "Strategic Relationships 360", h: "An entire group, its people, its assets, its unknowns, on a single screen.", img: "/home/news_360.jpg", to: "/platform" },
    { tick: "Attached evidence", label: "Attached evidence", h: "The rating rises on documents, not on promises.", img: "/home/news_evidence.jpg", to: "/pourquoi" },
    { tick: "Noam Camp", label: "Noam Camp", h: "Two hours, your documents, one connected world. Leave with the proof.", img: "/home/news_camp.jpg", to: "/camp" },
    { tick: "Morning Mirror", label: "Morning Mirror", h: "The daily brief that knows your files.", img: "/home/news_mirror.jpg", to: "/platform" },
  ],
  mission: "Our software powers real-time, AI-driven critical decisions, in banks, family offices and executive suites, from deal origination to the boardroom.",
  sw_ov: "Our software",
  sw_more: "Learn more",
  systems: [
    ["Private transactions end to end, from origination to closing.", "Brokers, intermediaries, family offices, banks"],
    ["Understand markets and steer investments.", "Managers, desks, investors, treasuries"],
    ["Steer the company, master operations, govern decisions.", "Executives, CFOs, risk & compliance officers"],
  ],
  plat_name: "Noam Platform",
  plat_sub: "The shared foundation: ontology, intelligence, AI, trust, network, data.",
  plat_link: "Explore the Platform",
  onto_ov: "The ontology",
  onto_t: "A connected world.",
  onto_lead: "Each object exists only once. Click: companies, deals, events and risks light up together.",
  grid: [
    ["Offerings", "Platform, transaction, infrastructure: your costs follow your deals.", "/request-access"],
    ["Impact studies", "The three problems Noam solves, and what they cost.", "/pourquoi"],
    ["Capabilities", "Ontology, intelligence, AI, trust, network, data.", "/platform"],
    ["Documents", "The technical document of the Noam infrastructure.", "/docs/noam-document-technique.html"],
  ],
  cta_l_t: "Request a demo",
  cta_l_s: "See Noam on your own files, not on a generic demo.",
  cta_r_t: "Join the Noam Camp",
  cta_r_s: "Two hours, your documents, one connected world. Leave with the proof.",
};

const SYS_META = [
  { k: "/01", name: "Noam Deal", img: "/home/sw_deal.jpg", world: "Private Markets & Transactions", world_fr: "Marchés privés & Transactions", tagline: "Originate. Connect. Execute.", tagline_fr: "Originer. Relier. Exécuter.", color: "#8E7CF8" },
  { k: "/02", name: "Noam Financial Markets", img: "/home/sw_markets.jpg", world: "Markets & Investments", world_fr: "Marchés & Investissements", tagline: "Observe. Understand. Decide.", tagline_fr: "Observer. Comprendre. Décider.", color: "#378ADD" },
  { k: "/03", name: "Noam Governance", img: "/home/sw_gov.jpg", world: "Enterprise Operations & Governance", world_fr: "Opérations & Gouvernance", tagline: "Understand. Control. Govern.", tagline_fr: "Comprendre. Contrôler. Gouverner.", color: "#1D9E75" },
];

function NewsCarousel({ items }) {
  const [idx, setIdx] = useState(0);
  const next = useCallback(() => setIdx((i) => (i + 1) % items.length), [items.length]);
  const prev = () => setIdx((i) => (i - 1 + items.length) % items.length);
  useEffect(() => {
    const t = setInterval(next, 6000);
    return () => clearInterval(t);
  }, [next]);
  const it = items[idx];
  return (
    <div data-testid="home-newsroom">
      <div className="flex items-center gap-6 overflow-x-auto pb-4 mb-4 border-b border-white/5" data-testid="home-news-ticker">
        {items.map((n, i) => (
          <button key={n.tick} onClick={() => setIdx(i)}
            className={`text-[11px] uppercase tracking-[0.2em] whitespace-nowrap transition-colors ${i === idx ? "text-white" : "text-zinc-600 hover:text-zinc-400"}`}
            data-testid={`home-news-tick-${i}`}>
            {n.tick}
          </button>
        ))}
      </div>
      <Link to={it.to} className="group block relative overflow-hidden border border-white/10" data-testid="home-news-card">
        <img src={it.img} alt={it.label} className="w-full h-[380px] sm:h-[460px] object-cover opacity-80 group-hover:opacity-95 group-hover:scale-[1.015] transition-all duration-700" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#05070A] via-[#05070A]/40 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-7 sm:p-10">
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#EF9F27]">{it.label}</div>
          <h2 className="noam-display text-2xl sm:text-4xl font-bold tracking-tight text-white mt-3 max-w-3xl leading-tight">{it.h}</h2>
        </div>
      </Link>
      <div className="flex items-center justify-between mt-4">
        <div className="flex gap-1.5">
          {items.map((_, i) => (
            <span key={i} className={`h-1 transition-all duration-300 ${i === idx ? "w-8 bg-[#43C6D8]" : "w-3 bg-white/15"}`} />
          ))}
        </div>
        <div className="flex gap-2">
          <button onClick={prev} className="p-2.5 border border-white/10 text-zinc-400 hover:text-white hover:border-white/30 transition-colors" data-testid="home-news-prev" aria-label="Précédent">
            <ArrowLeft className="w-4 h-4" />
          </button>
          <button onClick={next} className="p-2.5 border border-white/10 text-zinc-400 hover:text-white hover:border-white/30 transition-colors" data-testid="home-news-next" aria-label="Suivant">
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Home() {
  const { i18n } = useTranslation();
  const en = (i18n.language || "fr").startsWith("en");
  const L = en ? EN : FR;
  useSEO({ title: L.seo_t, description: L.seo_d, path: "/" });
  return (
    <div className="relative overflow-hidden" data-testid="home-root">
      {/* 1 · NEWSROOM — carrousel type Palantir */}
      <section className="relative">
        <div className="max-w-6xl mx-auto px-6 pt-10 pb-16">
          <NewsCarousel items={L.news} />
        </div>
      </section>

      {/* 2 · MISSION — une phrase, centrée */}
      <section className="relative border-t border-white/5 bg-black/40">
        <div className="max-w-4xl mx-auto px-6 py-24 sm:py-28 text-center" data-testid="home-mission">
          <p className="noam-display text-2xl sm:text-3xl lg:text-[2.6rem] font-bold tracking-tight leading-snug text-white">
            {L.mission}
          </p>
        </div>
      </section>

      {/* 3 · NOS LOGICIELS */}
      <section className="relative border-t border-white/5" id="systems">
        <div className="max-w-6xl mx-auto px-6 py-24">
          <h2 className="noam-display text-3xl sm:text-4xl font-bold tracking-tight mb-14" data-testid="home-software-title">{L.sw_ov}</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-white/5 border border-white/5">
            {SYS_META.map((s, i) => (
              <Link to="/platform" key={s.k}
                className="group bg-[#0A0E13] hover:bg-white/[0.02] hover:-translate-y-0.5 transition-all duration-300 block"
                data-testid={`home-system-${s.k.replace("/", "")}`}>
                <div className="relative h-44 overflow-hidden border-b border-white/5">
                  <img src={s.img} alt={s.name} className="noam-kenburns w-full h-full object-cover opacity-75 group-hover:opacity-95 transition-opacity duration-500" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0A0E13] via-transparent to-transparent" />
                  <div className="noam-scanline" />
                </div>
                <div className="px-7 pb-11 pt-6">
                <div className="flex items-center justify-between">
                  <span className="text-[10px] uppercase tracking-[0.25em] text-zinc-600 font-mono">{s.k}</span>
                  <span className="w-2 h-2 rounded-full" style={{ background: s.color }} />
                </div>
                <h3 className="noam-display text-2xl font-bold tracking-tight mt-6 text-white">{s.name}</h3>
                <div className="text-[11px] text-zinc-500 uppercase tracking-[0.12em] mt-1">{en ? s.world : s.world_fr}</div>
                <div className="text-xs uppercase tracking-[0.15em] mt-3" style={{ color: s.color }}>{en ? s.tagline : s.tagline_fr}</div>
                <p className="text-sm text-zinc-300 mt-5 leading-relaxed">{L.systems[i][0]}</p>
                <div className="text-[10px] text-zinc-600 mt-4">{L.systems[i][1]}</div>
                <div className="mt-7 inline-flex items-center gap-1.5 text-xs text-zinc-500 group-hover:text-[#43C6D8] transition-colors">
                  {L.sw_more} <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
                </div>
                </div>
              </Link>
            ))}
          </div>
          <div className="relative mt-px">
            <div className="grid grid-cols-3 gap-px px-px" aria-hidden="true">
              {[0, 1, 2].map((i) => (
                <div key={i} className="h-6 flex justify-center">
                  <span className="w-px h-full bg-gradient-to-b from-white/10 to-[#43C6D8]/50" />
                </div>
              ))}
            </div>
            <div className="border border-[#43C6D8]/25 bg-[#43C6D8]/[0.03] px-7 py-6 flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-testid="home-platform-band">
              <div>
                <span className="noam-display text-lg font-bold text-white">{L.plat_name}</span>
                <span className="text-xs text-zinc-500 ml-4 hidden sm:inline">{L.plat_sub}</span>
              </div>
              <Link to="/platform" className="text-xs text-[#43C6D8] flex items-center gap-1.5 hover:text-white transition-colors shrink-0" data-testid="home-platform-link">
                {L.plat_link} <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* 4 · ONTOLOGIE — le graphe signature */}
      <section className="relative border-t border-white/5 bg-black/40" id="ontology">
        <div className="max-w-6xl mx-auto px-6 py-24" data-testid="home-ontology-showcase">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
            <div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">{L.onto_ov}</div>
              <h2 className="noam-display text-3xl sm:text-4xl font-bold tracking-tight mt-4">{L.onto_t}</h2>
            </div>
            <p className="text-sm text-zinc-400 max-w-md leading-relaxed md:text-right">{L.onto_lead}</p>
          </div>
          <OntologyGraph />
        </div>
      </section>

      {/* 5 · GRILLE BAS DE PAGE — Offres / Impact / Capacités / Documents */}
      <section className="relative border-t border-white/5">
        <div className="max-w-6xl mx-auto px-6 py-20">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/5 border border-white/5" data-testid="home-bottom-grid">
            {L.grid.map((g, i) => {
              const external = g[2].startsWith("/docs/");
              const inner = (
                <>
                  <h3 className="noam-display text-lg font-bold tracking-tight text-white">{g[0]}</h3>
                  <p className="text-[13px] text-zinc-500 mt-3 leading-relaxed">{g[1]}</p>
                  <span className="mt-6 inline-flex items-center gap-1.5 text-xs text-zinc-500 group-hover:text-[#43C6D8] transition-colors">
                    <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
                  </span>
                </>
              );
              const cls = "group bg-[#0A0E13] px-6 py-9 hover:bg-white/[0.02] transition-colors flex flex-col";
              return external ? (
                <a key={g[0]} href={g[2]} target="_blank" rel="noreferrer" className={cls} data-testid={`home-grid-${i}`}>{inner}</a>
              ) : (
                <Link key={g[0]} to={g[2]} className={cls} data-testid={`home-grid-${i}`}>{inner}</Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* 6 · DOUBLE CTA GÉANT */}
      <section className="relative border-t border-white/5 bg-black/40">
        <div className="max-w-6xl mx-auto px-6 py-24">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-white/5 border border-white/5">
            <Link to="/request-access"
              className="group bg-[#0A0E13] px-8 sm:px-12 py-16 sm:py-20 transition-colors relative overflow-hidden"
              data-testid="home-giant-cta-demo">
              <img src="/home/cta_demo.jpg" alt="" className="absolute inset-0 w-full h-full object-cover opacity-20 group-hover:opacity-35 group-hover:scale-[1.03] transition-all duration-700" />
              <div className="absolute inset-0 bg-gradient-to-r from-[#0A0E13] via-[#0A0E13]/70 to-transparent" />
              <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-[#43C6D8]/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
              <div className="relative">
              <h3 className="noam-display text-3xl sm:text-4xl font-bold tracking-tight text-white group-hover:text-[#43C6D8] transition-colors flex items-center gap-4">
                {L.cta_l_t} <ArrowRight className="w-7 h-7 group-hover:translate-x-1.5 transition-transform" />
              </h3>
              <p className="text-sm text-zinc-400 mt-5 max-w-sm leading-relaxed">{L.cta_l_s}</p>
              </div>
            </Link>
            <Link to="/camp"
              className="group bg-[#0A0E13] px-8 sm:px-12 py-16 sm:py-20 transition-colors relative overflow-hidden"
              data-testid="home-giant-cta-camp">
              <img src="/home/news_camp.jpg" alt="" className="absolute inset-0 w-full h-full object-cover opacity-20 group-hover:opacity-35 group-hover:scale-[1.03] transition-all duration-700" />
              <div className="absolute inset-0 bg-gradient-to-r from-[#0A0E13] via-[#0A0E13]/70 to-transparent" />
              <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-[#EF9F27]/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
              <div className="relative">
              <h3 className="noam-display text-3xl sm:text-4xl font-bold tracking-tight text-white group-hover:text-[#EF9F27] transition-colors flex items-center gap-4">
                {L.cta_r_t} <ArrowRight className="w-7 h-7 group-hover:translate-x-1.5 transition-transform" />
              </h3>
              <p className="text-sm text-zinc-400 mt-5 max-w-sm leading-relaxed">{L.cta_r_s}</p>
              </div>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
